import { createTheme, rem } from "@mantine/core";

/**
 * Compliance platform theme.
 *
 * Restrained slate palette with indigo as the primary accent — appropriate
 * for regulator-facing dashboards. Customer brand colors are NOT baked in;
 * a deployment can override `primaryColor` per-customer config later.
 */
export const theme = createTheme({
  primaryColor: "indigo",
  primaryShade: { light: 6, dark: 4 },
  defaultRadius: "md",
  cursorType: "pointer",
  white: "#ffffff",
  black: "#0f172a",

  fontFamily:
    'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontFamilyMonospace:
    '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
  fontSmoothing: true,

  headings: {
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontWeight: "600",
    sizes: {
      h1: { fontSize: rem(28), lineHeight: "1.25" },
      h2: { fontSize: rem(22), lineHeight: "1.3" },
      h3: { fontSize: rem(18), lineHeight: "1.35" },
      h4: { fontSize: rem(16), lineHeight: "1.4", fontWeight: "600" },
      h5: { fontSize: rem(14), lineHeight: "1.4", fontWeight: "600" },
    },
  },

  // Mantine palette tweaks — a desaturated slate-tinted gray for a more
  // dashboard-y feel than the default cool gray.
  colors: {
    gray: [
      "#f8fafc",
      "#f1f5f9",
      "#e2e8f0",
      "#cbd5e1",
      "#94a3b8",
      "#64748b",
      "#475569",
      "#334155",
      "#1e293b",
      "#0f172a",
    ],
    // Dark-scheme palette mirrored from the omai main site
    // (omai-frontend-ui/src/scss/_variables-dark.scss). Mantine v8 derives the
    // dark scheme from this tuple: dark[0]→text, dark[2]→dimmed, dark[4]→default
    // border, dark[6]→Paper/Card/Menu surface, dark[7]→body background. So the
    // blue-tinted page (#1d2835) vs near-neutral cards (#28292b) split falls out
    // of the ordering — no extra --mantine-color-body override needed.
    dark: [
      "#c5c9ce", // 0  text       (~ rgba(255,255,255,0.75))
      "#a9aeb4", // 1
      "#8b9097", // 2  dimmed     (~ rgba(255,255,255,0.5))
      "#6c7178", // 3
      "#4a4f56", // 4  border     (~ rgba(255,255,255,0.25))
      "#33373c", // 5  hover
      "#28292b", // 6  surface (Paper/Card/Menu)  — omai component bg
      "#1d2835", // 7  body background           — omai page bg
      "#19222d", // 8
      "#141b24", // 9
    ],
    // Primary stays "indigo" in both schemes, but primaryShade is
    // { light: 6, dark: 4 } — so index 6 keeps the indigo light brand and index
    // 4 is omai's blue (#4e9cff), making the dark scheme read as omai without a
    // per-scheme primaryColor (which Mantine doesn't support).
    indigo: [
      "#edf2ff",
      "#dbe4ff",
      "#bac8ff",
      "#91a7ff",
      "#4e9cff", // 4  ← primaryShade.dark → omai blue
      "#5c7cfa",
      "#4c6ef5", // 6  ← primaryShade.light → indigo
      "#4263eb",
      "#3b5bdb",
      "#364fc7",
    ],
  },

  components: {
    Card: {
      defaultProps: { radius: "md", padding: "lg", withBorder: true, shadow: "xs" },
    },
    Paper: {
      defaultProps: { radius: "md", withBorder: true },
    },
    Button: {
      defaultProps: { radius: "md" },
    },
    Badge: {
      defaultProps: { radius: "sm" },
    },
    Table: {
      defaultProps: { striped: false, withColumnBorders: false, highlightOnHover: true },
    },
    Tabs: {
      defaultProps: { keepMounted: false },
    },
  },
});
